/**
 * Service Principal Hooks
 *
 * Feature: 023-service-principal-registry
 * Task: T015 - React Query hooks for SP registry
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Custom hooks for service principal data management using React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ServicePrincipal,
  SpWorkspaceAssignment,
  CreateServicePrincipalRequest,
  UpdateServicePrincipalRequest,
  CreateSpWorkspaceAssignmentRequest,
  ServicePrincipalFilters,
  AssignmentFilters,
  ReconciliationLogFilters,
  DriftFilters,
} from '../types';
import {
  servicePrincipalApi,
  getServicePrincipals,
  getServicePrincipalById,
  getServicePrincipalWithSummary,
  createServicePrincipal,
  updateServicePrincipal,
  deleteServicePrincipal,
  getAssignments,
  createAssignment,
  deleteAssignment,
  getDashboardSummary,
  getReconciliationStatus,
  triggerReconciliation,
  getReconciliationLogs,
  getActiveDrifts,
  fixDrift,
  resolveDrift,
  getExportHistory,
  triggerExport,
} from '../services/servicePrincipalApi';

// Query keys for cache management
export const servicePrincipalKeys = {
  all: ['service-principals'] as const,
  lists: () => [...servicePrincipalKeys.all, 'list'] as const,
  list: (filters?: ServicePrincipalFilters) => [...servicePrincipalKeys.lists(), filters] as const,
  details: () => [...servicePrincipalKeys.all, 'detail'] as const,
  detail: (id: string) => [...servicePrincipalKeys.details(), id] as const,
  detailWithSummary: (id: string) => [...servicePrincipalKeys.details(), id, 'summary'] as const,
  assignments: (spId: string) => [...servicePrincipalKeys.all, 'assignments', spId] as const,
  dashboard: () => [...servicePrincipalKeys.all, 'dashboard'] as const,
  reconciliation: () => [...servicePrincipalKeys.all, 'reconciliation'] as const,
  reconciliationStatus: () => [...servicePrincipalKeys.reconciliation(), 'status'] as const,
  reconciliationLogs: (filters?: ReconciliationLogFilters) =>
    [...servicePrincipalKeys.reconciliation(), 'logs', filters] as const,
  drifts: (filters?: DriftFilters) => [...servicePrincipalKeys.all, 'drifts', filters] as const,
  exports: () => [...servicePrincipalKeys.all, 'exports'] as const,
};

// =============================================================================
// Service Principal Queries
// =============================================================================

/**
 * Hook to fetch list of service principals
 */
export function useServicePrincipals(filters?: ServicePrincipalFilters) {
  return useQuery({
    queryKey: servicePrincipalKeys.list(filters),
    queryFn: () => getServicePrincipals(filters),
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch single service principal by ID
 */
export function useServicePrincipal(id: string) {
  return useQuery({
    queryKey: servicePrincipalKeys.detail(id),
    queryFn: () => getServicePrincipalById(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch service principal with assignments summary
 */
export function useServicePrincipalWithSummary(id: string) {
  return useQuery({
    queryKey: servicePrincipalKeys.detailWithSummary(id),
    queryFn: () => getServicePrincipalWithSummary(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch assignments for a service principal
 */
export function useAssignments(servicePrincipalId: string, filters?: AssignmentFilters) {
  return useQuery({
    queryKey: servicePrincipalKeys.assignments(servicePrincipalId),
    queryFn: () => getAssignments(servicePrincipalId, filters),
    enabled: !!servicePrincipalId,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch dashboard summary
 */
export function useDashboardSummary() {
  return useQuery({
    queryKey: servicePrincipalKeys.dashboard(),
    queryFn: getDashboardSummary,
    staleTime: 60000, // 1 minute
    refetchInterval: 60000, // Auto-refresh every minute
  });
}

// =============================================================================
// Service Principal Mutations
// =============================================================================

/**
 * Hook to create a new service principal
 */
export function useCreateServicePrincipal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateServicePrincipalRequest) => createServicePrincipal(data),
    onSuccess: () => {
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.dashboard() });
    },
  });
}

/**
 * Hook to update a service principal
 */
export function useUpdateServicePrincipal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServicePrincipalRequest }) =>
      updateServicePrincipal(id, data),
    onSuccess: (result) => {
      // Update cached detail
      queryClient.setQueryData(servicePrincipalKeys.detail(result.id), result);
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.dashboard() });
    },
  });
}

/**
 * Hook to delete a service principal
 */
export function useDeleteServicePrincipal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteServicePrincipal(id),
    onSuccess: (_data, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: servicePrincipalKeys.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.dashboard() });
    },
  });
}

// =============================================================================
// Assignment Mutations
// =============================================================================

/**
 * Hook to create a workspace assignment
 */
export function useCreateAssignment(servicePrincipalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSpWorkspaceAssignmentRequest) =>
      createAssignment(servicePrincipalId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: servicePrincipalKeys.assignments(servicePrincipalId),
      });
      queryClient.invalidateQueries({
        queryKey: servicePrincipalKeys.detailWithSummary(servicePrincipalId),
      });
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.dashboard() });
    },
  });
}

/**
 * Hook to delete a workspace assignment
 */
export function useDeleteAssignment(servicePrincipalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) => deleteAssignment(servicePrincipalId, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: servicePrincipalKeys.assignments(servicePrincipalId),
      });
      queryClient.invalidateQueries({
        queryKey: servicePrincipalKeys.detailWithSummary(servicePrincipalId),
      });
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.dashboard() });
    },
  });
}

// =============================================================================
// Reconciliation Queries & Mutations
// =============================================================================

/**
 * Hook to get reconciliation status
 */
export function useReconciliationStatus() {
  return useQuery({
    queryKey: servicePrincipalKeys.reconciliationStatus(),
    queryFn: getReconciliationStatus,
    staleTime: 10000, // 10 seconds
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Hook to get reconciliation logs
 */
export function useReconciliationLogs(filters?: ReconciliationLogFilters) {
  return useQuery({
    queryKey: servicePrincipalKeys.reconciliationLogs(filters),
    queryFn: () => getReconciliationLogs(filters),
    staleTime: 30000,
  });
}

/**
 * Hook to trigger reconciliation
 */
export function useTriggerReconciliation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (type?: 'daily_permissions' | 'weekly_entra' | 'weekly_export') =>
      triggerReconciliation(type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.reconciliation() });
    },
  });
}

// =============================================================================
// Drift Queries & Mutations
// =============================================================================

/**
 * Hook to get active drifts
 */
export function useActiveDrifts(filters?: DriftFilters) {
  return useQuery({
    queryKey: servicePrincipalKeys.drifts(filters),
    queryFn: () => getActiveDrifts(filters),
    staleTime: 30000,
    refetchInterval: 60000, // Refresh every minute
  });
}

/**
 * Hook to fix a drift
 */
export function useFixDrift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (driftId: string) => fixDrift(driftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.drifts() });
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.reconciliation() });
    },
  });
}

/**
 * Hook to resolve a drift manually
 */
export function useResolveDrift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ driftId, resolution }: { driftId: string; resolution: string }) =>
      resolveDrift(driftId, resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.drifts() });
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.dashboard() });
    },
  });
}

// =============================================================================
// Export Queries & Mutations
// =============================================================================

/**
 * Hook to get export history
 */
export function useExportHistory() {
  return useQuery({
    queryKey: servicePrincipalKeys.exports(),
    queryFn: getExportHistory,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to trigger manual export
 */
export function useTriggerExport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerExport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: servicePrincipalKeys.exports() });
    },
  });
}
